/**
 * "이 화면은 왜 뒤처져 있나" 를 사람 말로 옮긴다.
 *
 * **`behind` 만으로는 아무도 다음 수를 정할 수 없다.** 버전이 안 올라가는 이유는
 * 셋인데 조치가 전부 다르다.
 *
 *   - **묻지 않는다**(`idle`·`checking` 에서 멈춤 · `unsupported`) — 화면이 서버에
 *     물어보지도 않는다. 에이전트를 한 번 손으로 올려야 고리가 끊긴다.
 *   - **받다 실패한다**(`failed`) — 회선이나 저장 공간을 본다.
 *   - **사람이 눌러야 끝난다**(`needs-confirm`) — 시스템이 설치 확인 창을 요구하는
 *     안드로이드다. 현장에 가야 한다.
 *
 * 이 셋을 한 덩어리로 "업데이트 안 됨" 이라고 적으면, 운영자는 매번 셋 다 해 보게 된다.
 * 그중 둘은 헛걸음이고, 헛걸음이 반복되면 그 경고는 곧 무시된다.
 */

export interface AgentUpdateState {
  phase: string;
  version: string | null;
  detail: string | null;
  at: string;
}

export interface AgentState {
  wanted: string | null;
  behind: boolean;
  update?: AgentUpdateState | null;
}

export interface BehindReason {
  /** 배지에 들어갈 짧은 말. */
  label: string;
  /** 무엇을 해야 하는지. 도움말로 띄운다. */
  hint: string;
  /** 사람이 가야 풀리는 일인가 — 그때만 붉게 낸다. */
  tone: 'danger' | 'warn';
}

/**
 * 뒤처진 화면에 붙일 말. 뒤처지지 않았으면 `null` 이다.
 *
 * **모르면 모른다고 한다.** 옛 플레이어는 단계를 보내지 않는다. 그때 "묻지 않는다"
 * 고 단정하면 멀쩡히 설치를 기다리는 화면에 사람을 보내게 된다.
 */
export function behindReason(agent: AgentState | undefined): BehindReason | null {
  if (!agent?.behind) return null;
  const want = agent.wanted ? `v${agent.wanted}` : '새 버전';

  switch (agent.update?.phase) {
    case 'needs-confirm':
      return {
        label: `${want} 확인 대기`,
        /*
         * **원인을 단정하지 않는다.**
         *
         * 예전에는 "기기 소유자(device owner)가 아니라서" 라고 적고 프로비저닝을 권했다.
         * 그런데 기기 소유자로 지정된 안드로이드 5.1 에서도 시스템이 확인 창을 요구했다
         * (2026-09-21). 그 문구를 믿은 사람은 이미 소유자인 기기를 다시 프로비저닝하러
         * 현장에 간다 — 헛걸음이고, 다녀와도 같은 자리에 선다.
         *
         * 그래서 **확실한 것만** 말한다: 이 기기는 확인 없이 깔지 못하고, 지금 할 수 있는
         * 일은 정비 모드의 [지금 업데이트] 다. 프로비저닝은 "대개" 로 덧붙인다.
         */
        hint: '이 기기는 사람이 확인해야 설치됩니다. 현장에서 기기 정비 모드의 [지금 업데이트] 로 설치하세요(0.6.3 이하는 설치 확인 창이 떠 있을 수 있습니다). 안드로이드 12 이상은 에이전트 1.1.2 부터 이 앱의 ‘출처를 알 수 없는 앱 설치’ 가 켜져 있으면 다음 판부터 스스로 설치합니다. 기기 소유자(device owner)로 프로비저닝하면 대개 무인 설치가 되지만, 소유자인데도 시스템이 확인을 요구하는 기기가 있습니다(안드로이드 5.x 등) — 그 기기는 버전을 올릴 때마다 현장에서 눌러야 합니다.',
        tone: 'danger',
      };
    case 'failed':
      return {
        label: `${want} 설치 실패`,
        hint: agent.update.detail
          ? `마지막 시도: ${agent.update.detail}`
          : '내려받기나 설치가 실패했습니다. 회선과 저장 공간을 확인하세요.',
        tone: 'danger',
      };
    case 'installing':
      return { label: `${want} 설치 중`, hint: '설치가 진행 중입니다. 끝나면 화면이 다시 시작됩니다.', tone: 'warn' };
    case 'available':
      /*
       * 둘 중 하나다 — 설치 시간대를 기다리거나(0.7.17+, 화면이 그 사실을 `detail` 로 말한다), 자동 업데이트가 꺼져 있다.
       * 앞의 것은 기다리면 스스로 깔리고, 뒤의 것은 누군가 켜거나 눌러야 한다.
       */
      return agent.update.detail
        ? { label: `${want} 설치 예약`, hint: agent.update.detail, tone: 'warn' }
        : { label: `${want} 대기`, hint: '자동 업데이트가 꺼져 있어 받아만 두었습니다.', tone: 'warn' };
    case 'unsupported':
      return {
        label: `${want} 미적용`,
        hint: '이 화면은 스스로 갈아끼울 수단이 없습니다(브라우저 키오스크). 에이전트로 설치해야 합니다.',
        tone: 'danger',
      };
    default:
      /*
       * `idle`·`checking`·`current` 로 머물러 있는데 뒤처졌다면 **묻지 않고 있는 것**이다.
       * 단계를 아예 보내지 않는 옛 플레이어도 여기로 온다 — 어느 쪽이든 다음 수는 같다.
       */
      return {
        label: `${want} 미적용`,
        hint: '화면이 업데이트를 묻지 않고 있습니다. 현장에서 정비 모드 → [지금 확인] → [지금 업데이트] 로 한 번 올리면 이후로는 스스로 따라옵니다.',
        tone: 'danger',
      };
  }
}
